import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Divider, Typography } from '@mui/material';
import { LayoutGridItem } from 'app/layout/LayoutGrid';
import { ScrolledText } from '@AMIEWEB/Facility/FacilityDetails/FacilityTabs/GeneralTab/Utils';
import { missingField } from 'app/constants';
import { makeStyles } from 'tss-react/mui';
import { useDispatch, useSelector } from 'react-redux';
import {
  selectAdditionalRequiredText,
  selectNewOrderCreation,
  selectPage2,
  selectedOrderTypeDetails,
  selectedUnitSkillSets,
} from '@AMIEWEB/NewOrder/Store/Order.selectors';
import { getAdditionalQualificationString, getQualificationStringExperience } from '../../helper';
import { orderCreationNewActions } from '@AMIEWEB/NewOrder/Store/OrderCreation.actions';
import { selectOrderDetailsUIView } from '@AMIEWEB/Order/Store/Order.selectors';
import { IOrderFreeText } from 'app/models/Orders/OrderDetails';
import { OrderRequirementsProps } from '../OrderRequirements';
import { QualificationType, QualificationTypeString } from '@AMIEWEB/NewOrder/constants';
import { Concatenate } from 'utils/string/string';
import QualificationModalEnhanced from './QualificationModalEnhanced';

/**
 * shared style also used in other components
 */
export const useQualificationStyles = makeStyles()(theme => ({
  subSectionHeading: {
    fontSize: '16px !important',
    color: theme.palette.system.black,
    fontWeight: 500,
  },
  // eslint-disable-next-line tss-unused-classes/unused-classes
  facilityGeneralLaunchIcon: {
    cursor: 'pointer !important',
    color: theme.palette.system.neutralGray,
    width: '25px',
    padding: '2px',
    '&:hover': {
      color: theme.palette.system.steelBlue,
      borderRadius: '50%',
      background: theme.palette.system.lightSteelBlue,
    },
  },
}));

const QualificationDetail = ({
  title,
  text,
  onView,
  type,
}: {
  title: string;
  text: string;
  onView?: (type: QualificationType) => void;
  type: QualificationType;
}) => {
  const { classes } = useQualificationStyles();
  const trimmedText = text?.trim();
  return (
    <>
      <LayoutGridItem xs={11} item>
        <Typography className={classes.subSectionHeading}>{title}</Typography>
      </LayoutGridItem>
      <LayoutGridItem
        xs={1}
        sx={{ paddingLeft: '2px !important', display: 'flex', flexDirection: 'row-reverse' }}
        item
      ></LayoutGridItem>
      <LayoutGridItem sx={{ paddingBottom: '12px !important', display: 'flex', width: '100%' }} item>
        {trimmedText ? <ScrolledText text={trimmedText} maxHeight={117} /> : missingField}
      </LayoutGridItem>
    </>
  );
};

const Qualifications = (props: OrderRequirementsProps) => {
  const {
    isNewOrderCreation,
    isEditQualification,
    orderCreationEdit,
    handleCloseModal,
    isOpenFromDetailsPage,
    isOpenFromPreview,
  } = props;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  let qualificationDetails = {} as IOrderFreeText;
  let orderDetails = useSelector(selectNewOrderCreation) as IOrderFreeText;
  const orderDetailsCopy = useSelector(selectNewOrderCreation); //These selectors needs to be refactored @Karthik.Amaresh
  const orderDetailsEdit = useSelector(selectOrderDetailsUIView)?.orderDetails;
  const additionalText = useSelector(selectAdditionalRequiredText);

  if (!isNewOrderCreation) {
    qualificationDetails = {
      orderId: orderDetailsEdit.orderId,
      additionalRequiredQualifications: orderDetailsEdit.additionalRequiredQualifications ?? '',
      jobDescription: orderDetailsEdit.jobDescription ?? '',
      internalNotes: orderDetailsEdit.internalNotes ?? '',
      preferredQualifications: orderDetailsEdit.preferredQualifications ?? '',
      requiredQualifications: orderDetailsEdit.requiredQualifications ?? '',
    } as IOrderFreeText;
    orderDetails = qualificationDetails;
  }
  const selectedAddtionalRequirement = useSelector(selectPage2);
  const createOrderState = useSelector(selectedOrderTypeDetails);
  const selectedUnitSkillsets = useSelector(selectedUnitSkillSets);

  let [requiredQualificationText, preferredQualificationText] = isNewOrderCreation
    ? getQualificationStringExperience(
        orderDetails,
        createOrderState.skillSetGroup,
        selectedUnitSkillsets,
        orderDetailsCopy?.page3?.additionalInformation,
      )
    : [qualificationDetails.requiredQualifications, qualificationDetails.preferredQualifications];

  if (isNewOrderCreation) {
    requiredQualificationText = Concatenate(
      [
        requiredQualificationText,
        getAdditionalQualificationString(
          createOrderState.ssnRequired,
          createOrderState.dobRequired,
          selectedAddtionalRequirement.orderWorkHistory,
          selectedAddtionalRequirement.reference,
        ),
        `\n${additionalText}`,
      ],
      '\n',
    );
    qualificationDetails.requiredQualifications = requiredQualificationText;
    qualificationDetails.additionalRequiredQualifications = additionalText;
  }

  useEffect(() => {
    if (isNewOrderCreation) {
      dispatch(
        orderCreationNewActions.setQualificationText({
          name: QualificationTypeString[QualificationType.Required],
          value: requiredQualificationText,
        }),
      );
      dispatch(
        orderCreationNewActions.setQualificationText({
          name: QualificationTypeString[QualificationType.AdditionalRequiredQualifications],
          value: isNewOrderCreation ? additionalText : qualificationDetails.additionalRequiredQualifications,
        }),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getText = {
    [QualificationType.Required]: orderDetails?.requiredQualifications,
    [QualificationType.Preferred]: orderDetails?.preferredQualifications,
    [QualificationType.AdditionalRequiredQualifications]: orderDetails?.additionalRequiredQualifications,
    [QualificationType.InternalNotes]: orderDetails?.internalNotes,
    [QualificationType.ExternalJobDetails]: orderDetails?.jobDescription,
  };

  const handleClose = () => {
    handleCloseModal();
  };

  return (
    <>
      <LayoutGridItem container item rowSpacing={2}>
        <QualificationDetail
          title={t('order.orderDetails.freeText.requiredQualification')}
          text={getText[QualificationType.Required]}
          type={QualificationType.Required}
        />
        <Divider sx={{ width: '100%' }} />
        <QualificationDetail
          title={t('order.orderDetails.freeText.additionalRequiredQualifications')}
          text={getText[QualificationType.AdditionalRequiredQualifications]}
          type={QualificationType.AdditionalRequiredQualifications}
        />
        <Divider sx={{ width: '100%' }} />
        <QualificationDetail
          title={t('order.orderDetails.freeText.preferredQualification')}
          text={getText[QualificationType.Preferred]}
          type={QualificationType.Preferred}
        />
        <Divider sx={{ width: '100%' }} />
        <QualificationDetail
          title={t('order.orderDetails.freeText.internalNotes')}
          text={getText[QualificationType.InternalNotes]}
          type={QualificationType.InternalNotes}
        />
        <Divider sx={{ width: '100%' }} />
        <QualificationDetail
          title={t('facility.units.jobDetails.externalJobDescription')}
          text={getText[QualificationType.ExternalJobDetails]}
          type={QualificationType.ExternalJobDetails}
        />
      </LayoutGridItem>
      {(isOpenFromPreview || isOpenFromDetailsPage) && (
        <QualificationModalEnhanced
          open={isOpenFromDetailsPage || isOpenFromPreview}
          onClose={() => {
            handleClose();
          }}
          title={t('order.createUnit.qualifications.title')}
          isNewOrderCreation={isNewOrderCreation}
          isEditQualification={isEditQualification}
          orderCreationEdit={orderCreationEdit}
          details={{ orderDetails }}
        />
      )}
    </>
  );
};

export default Qualifications;
